// Agent selector — small floating panel on the lobby.
// Persists selection to settingsStore.selectedAgent, character auto-updates.
import { useSettings } from "@/stores/settingsStore";
import { AGENT_ORDER, getAgent, AGENTS } from "@/game/agents/agents";
import { useUiSounds } from "@/lib/audio";
import { toast } from "sonner";
import { Users2 } from "lucide-react";

export default function AgentSelector() {
    const sel = useSettings((s) => s.selectedAgent) || "recruit";
    const update = useSettings((s) => s.update);
    const sfx = useUiSounds();
    const active = getAgent(sel);

    const pick = (id) => {
        sfx.click();
        update({ selectedAgent: id });
        toast.success(`Agent · ${AGENTS[id].name}`);
    };

    return (
        <div
            data-testid="agent-selector"
            className="pointer-events-auto axis-glass axis-cut-sm px-3 py-3 border border-white/15 max-w-[220px]"
        >
            <div className="flex items-center gap-2 mb-2">
                <Users2 size={12} className="text-orange-300" />
                <span className="axis-mono text-[10px] tracking-[0.4em] text-white/60">
                    AGENT
                </span>
            </div>
            <div className="axis-heading text-base tracking-widest text-white">
                {active.name}
            </div>
            <div className="axis-mono text-[9px] tracking-[0.3em] text-orange-300/80 mt-0.5">
                {active.tag.toUpperCase()}
            </div>
            {/* Perk block */}
            {active.passive && (
                <div className="mt-2 axis-cut-sm border border-orange-400/30 bg-orange-500/5 px-2.5 py-1.5">
                    <div className="axis-mono text-[9px] tracking-[0.3em] text-orange-300/90">
                        PERK · {active.passive.perkTitle}
                    </div>
                    <div className="axis-mono text-[10px] text-white/70 mt-0.5 leading-snug">
                        {active.passive.perkDesc}
                    </div>
                </div>
            )}
            <div className="mt-3 space-y-1.5">
                {AGENT_ORDER.map((id) => {
                    const a = AGENTS[id];
                    const isActive = id === sel;
                    return (
                        <button
                            key={id}
                            data-testid={`agent-${id}`}
                            onClick={() => pick(id)}
                            onMouseEnter={sfx.hover}
                            className={`w-full axis-cut-sm px-2.5 py-2 flex items-center gap-2 border transition text-left ${
                                isActive
                                    ? "bg-white/10 border-white text-white"
                                    : "border-white/10 bg-black/30 text-white/60 hover:text-white hover:border-white/40"
                            }`}
                        >
                            <span
                                className="w-3 h-3 axis-cut-sm border"
                                style={{
                                    background: a.palette.vest,
                                    borderColor: a.palette.beltAccent,
                                }}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="axis-heading text-[11px] tracking-widest">
                                    {a.name}
                                </div>
                                <div className="axis-mono text-[9px] tracking-[0.25em] text-white/40 truncate">
                                    {a.tag}
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
